import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Wallet as WalletIcon, Plus, TrendingUp, TrendingDown, X, Calendar, FileText, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Wallets() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [walletForm, setWalletForm] = useState({
    wallet_name: '',
    wallet_type: 'banco_plaza',
    currency: 'VES'
  });

  useEffect(() => {
    loadWallets();
  }, []);

  const loadWallets = async () => {
    try {
      const token = localStorage.getItem('token');
      const projectId = localStorage.getItem('activeProjectId');
      
      if (!projectId || projectId === 'all') {
        setWallets([]);
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/projects/${projectId}/wallets`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWallets(response.data);
    } catch (error) {
      console.error('Error loading wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const projectId = localStorage.getItem('activeProjectId');

      await axios.post(
        `${BACKEND_URL}/api/wallets`,
        { ...walletForm, project_id: projectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowModal(false);
      setWalletForm({ wallet_name: '', wallet_type: 'banco_plaza', currency: 'VES' });
      loadWallets();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear wallet');
    }
  };

  const loadWalletHistory = async (wallet) => {
    setSelectedWallet(wallet);
    setShowHistoryModal(true);
    setLoadingTransactions(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Llamar endpoint para obtener transacciones de la wallet
      const response = await axios.get(
        `${BACKEND_URL}/api/wallets/${wallet.wallet_id}/transactions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setWalletTransactions(response.data || []);
    } catch (error) {
      console.error('Error loading wallet transactions:', error);
      // Si el endpoint no existe, buscar en MongoDB directamente como fallback
      setWalletTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const activeProjectId = localStorage.getItem('activeProjectId');

  if (!activeProjectId || activeProjectId === 'all') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-12">
          <div className="text-center">
            <WalletIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Selecciona un Proyecto</h2>
            <p className="text-gray-600">Las wallets son específicas de cada proyecto. Selecciona un proyecto en el header para ver sus wallets.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Wallets</h1>
            <p className="text-sm text-gray-600 mt-1">Gestiona tus billeteras digitales</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: '#003594' }}
          >
            <Plus className="w-4 h-4" />
            Nueva Wallet
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : wallets.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-gray-200">
            <WalletIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay wallets</h3>
            <p className="text-gray-600 mb-6">Crea tu primera wallet para comenzar</p>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 text-white rounded-lg"
              style={{ backgroundColor: '#003594' }}
            >
              Crear Primera Wallet
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wallets.filter(w => w.wallet_type !== 'commission' && !w.wallet_name.toLowerCase().includes('comision')).map((wallet) => (
              <div 
                key={wallet.wallet_id} 
                className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => loadWalletHistory(wallet)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: '#051C2C' }}>
                      {wallet.wallet_name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {wallet.wallet_type.replace('_', ' ').toUpperCase()}
                    </p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    {wallet.status}
                  </span>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">Balance Actual</p>
                  <p className="text-3xl font-bold" style={{ color: '#003594' }}>
                    {wallet.currency} {wallet.balance.toFixed(2)}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <p className="text-xs text-gray-600">Recibido</p>
                    </div>
                    <p className="text-sm font-semibold text-green-600">
                      +{wallet.total_received.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingDown className="w-4 h-4 text-red-600" />
                      <p className="text-xs text-gray-600">Enviado</p>
                    </div>
                    <p className="text-sm font-semibold text-red-600">
                      -{wallet.total_sent.toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      loadWalletHistory(wallet);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" />
                    Ver Historial de Transacciones
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Wallet Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>Nueva Wallet</h2>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={walletForm.wallet_name}
                  onChange={(e) => setWalletForm({ ...walletForm, wallet_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Wallet Principal"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={walletForm.wallet_type}
                  onChange={(e) => setWalletForm({ ...walletForm, wallet_type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="banco_plaza">Banco Plaza</option>
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="crypto">Crypto</option>
                  <option value="internal">Interna</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                <select
                  value={walletForm.currency}
                  onChange={(e) => setWalletForm({ ...walletForm, currency: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="VES">VES (Bolívares)</option>
                  <option value="USD">USD (Dólares)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white rounded-lg"
                  style={{ backgroundColor: '#003594' }}
                >
                  Crear Wallet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Historial de Transacciones Modal */}
      {showHistoryModal && selectedWallet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHistoryModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b" style={{ backgroundColor: '#F8FAFC' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#051C2C' }}>
                    Historial de Transacciones
                  </h2>
                  <p className="text-gray-600 mt-1">{selectedWallet.wallet_name}</p>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Balance Info */}
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-xs text-gray-600 mb-1">Balance Actual</p>
                  <p className="text-2xl font-bold" style={{ color: '#003594' }}>
                    {formatCurrency(selectedWallet.balance)}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-xs text-gray-600 mb-1">Total Recibido</p>
                  <p className="text-xl font-bold text-green-600">
                    +{formatCurrency(selectedWallet.total_received)}
                  </p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-xs text-gray-600 mb-1">Total Enviado</p>
                  <p className="text-xl font-bold text-red-600">
                    -{formatCurrency(selectedWallet.total_sent)}
                  </p>
                </div>
              </div>
            </div>

            {/* Transactions List */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
              {loadingTransactions ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Cargando transacciones...</p>
                </div>
              ) : walletTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No hay transacciones registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {walletTransactions.map((tx) => (
                    <div key={tx.transaction_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {tx.amount > 0 ? (
                              <ArrowDownCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <ArrowUpCircle className="w-5 h-5 text-red-600" />
                            )}
                            <div>
                              <p className="font-semibold" style={{ color: '#051C2C' }}>
                                {tx.description}
                              </p>
                              <p className="text-xs text-gray-500">
                                {tx.type?.replace('_', ' ').toUpperCase()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 text-xs text-gray-600 ml-8">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(tx.created_at)}
                            </div>
                            {tx.payment_reference && (
                              <div className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                Ref: {tx.payment_reference}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.amount > 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-gray-50">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <p>Total de transacciones: {walletTransactions.length}</p>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
